import React from "react";
import MDBox from "../../../../materialDashboard/components/MDBox";
import {
    usePublicIncidentQuery,
    useIncidentRegistryQuery,
    useRegionQuery,
    useProvinceQuery,
    useMunicipalityQuery,
    usePublicIncidentAttachmentsQuery
} from "../query";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import FeatureSelectionChips from "./FeatureSelectionChips";
import useActiveFeature from "./useActiveFeature";
import {useTranslation} from "react-i18next";
import {Grid, Typography, Divider, List, ListItem, ListItemText, IconButton, Tooltip} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {format} from "date-fns";

const IncidentPanel = ({selectedFeatures, onDeSelectFeature}) => {
    const {t} = useTranslation(['webgis']);
    const {activeFeatureId, setActiveFeatureId} = useActiveFeature(selectedFeatures);

    const incidentQuery = usePublicIncidentQuery(activeFeatureId);
    const incident = incidentQuery.data;

    const attachmentsQuery = usePublicIncidentAttachmentsQuery(activeFeatureId);
    const attachments = attachmentsQuery.data || [];

    const incidentRegistryQuery = useIncidentRegistryQuery();
    const incidentRegistry = incidentRegistryQuery.data;

    const regionQuery = useRegionQuery(incident?.regionId);
    const provinceQuery = useProvinceQuery(incident?.provinceId);
    const municipalityQuery = useMunicipalityQuery(incident?.municipalityId);

    if (!activeFeatureId) {
        return null;
    }

    if (incidentQuery.isLoading || incidentRegistryQuery.isLoading || attachmentsQuery.isLoading) {
        return <MDBox sx={{position: 'relative', height: '200px'}}><LoadingOverlay /></MDBox>;
    }

    const getRegistryDescription = (registryKey, code) => {
        if (!incidentRegistry || !code) return code;
        const items = incidentRegistry[registryKey] || [];
        const item = items.find(i => i.code === code);
        return item ? item.description : code;
    };

    const infoItem = (label, value) => (
        <Grid item xs={12} sm={6} md={4}>
            <MDBox mb={1}>
                <Typography variant="caption" fontWeight="bold" color="text">
                    {label}:
                </Typography>
                <Typography variant="body2" color="text">
                    {value || '-'}
                </Typography>
            </MDBox>
        </Grid>
    );

    const getIncidentLabel = (feature) => {
        let label;
        if (feature && feature.properties && feature.properties.locationName) {
            label = `${feature.properties.locationName}`;
        } else {
            label = `${feature.properties.id}`;
        }
        if (feature && feature.properties && feature.properties.date) {
            label += ` - ${format(new Date(feature.properties.date), 'dd/MM/yyyy')}`;
        }
        return label;
    }

    return (
        <MDBox>
            <FeatureSelectionChips
                selectedFeatures={selectedFeatures}
                activeFeatureId={activeFeatureId}
                setActiveFeatureId={setActiveFeatureId}
                onDeSelectFeature={onDeSelectFeature}
                sx={{ mb: 2 }}
                getLabel={getIncidentLabel}
            />

            {incident && (
                <MDBox sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('incidentPanel.localizationTitle')}
                    </Typography>
                    <Grid container spacing={2}>
                        {infoItem(t('incidentPanel.localization.date'), incident.date ? format(new Date(incident.date), 'dd/MM/yyyy') : '-')}
                        {infoItem(t('incidentPanel.localization.region'), regionQuery.data?.name)}
                        {infoItem(t('incidentPanel.localization.province'), provinceQuery.data?.name)}
                        {infoItem(t('incidentPanel.localization.municipality'), municipalityQuery.data?.name)}
                        {infoItem(t('incidentPanel.localization.locationName'), incident.locationName)}
                        {infoItem(t('incidentPanel.localization.elevationRelease'), incident.elevationRelease ? `${incident.elevationRelease} m` : '-')}
                        {infoItem(t('incidentPanel.localization.aspect'), getRegistryDescription('aspect', incident.aspect))}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        {t('incidentPanel.involvedPeopleTitle')}
                    </Typography>
                    <Grid container spacing={2}>
                        {infoItem(t('incidentPanel.involvedPeople.numberOfPeople'), incident.numberOfPeopleInvolved)}
                        {infoItem(t('incidentPanel.involvedPeople.numberOfPeopleCaught'), incident.numberOfPeopleCaught)}
                        {infoItem(t('incidentPanel.involvedPeople.numberOfUninjured'), incident.numberOfUninjured)}
                        {infoItem(t('incidentPanel.involvedPeople.numberOfInjured'), incident.numberOfInjured)}
                        {infoItem(t('incidentPanel.involvedPeople.numberOfDeaths'), incident.numberOfDeaths)}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        {t('incidentPanel.conditionsTitle')}
                    </Typography>
                    <Grid container spacing={2}>
                        {infoItem(t('incidentPanel.conditions.avalancheType'), getRegistryDescription('avalancheType', incident.avalancheType))}
                        {infoItem(t('incidentPanel.conditions.snowHeightMin'), incident.snowHeightMin)}
                        {infoItem(t('incidentPanel.conditions.snowHeightMax'), incident.snowHeightMax)}
                        {infoItem(t('incidentPanel.conditions.slopeAngleRelease'), getRegistryDescription('slopeAngleReleaseRange', incident.slopeAngleReleaseRange))}
                        {infoItem(t('incidentPanel.conditions.dangerScaleBulletin'), getRegistryDescription('dangerScale', incident.dangerScaleBulletin))}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        {t('incidentPanel.incidentTitle')}
                    </Typography>
                    <Grid container spacing={2}>
                        {infoItem(t('incidentPanel.incident.incidentCategory'), getRegistryDescription('incidentCategoryV2', incident.incidentCategory))}
                        {infoItem(t('incidentPanel.incident.incidentSubcategory'), getRegistryDescription('incidentSubcategory', incident.incidentSubcategory))}
                    </Grid>

                    {attachments && attachments.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                {t('incidentPanel.attachmentsTitle')}
                            </Typography>
                            <List disablePadding>
                                {attachments.map((attachment) => (
                                    <ListItem
                                        key={attachment.id}
                                        divider
                                        secondaryAction={
                                            (attachment?.file?.path || attachment?.webLink) && (
                                                <Tooltip title={attachment?.webLink ? t('incidentPanel.attachments.openLink') : t('incidentPanel.attachments.download')}>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label={attachment?.webLink ? "open link" : "download"}
                                                        component="a"
                                                        href={attachment?.webLink || attachment.file.path}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {attachment?.webLink ? <OpenInNewIcon /> : <DownloadIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        }
                                    >
                                        <ListItemText
                                            primary={attachment.description}
                                            primaryTypographyProps={{
                                                variant: "caption",
                                                fontWeight: "bold",
                                            }}
                                            secondary={
                                                <>
                                                    <Typography component="span" variant="caption" color="text">
                                                        {t('incidentPanel.attachments.type')}: {getRegistryDescription('attachmentType', attachment.attachmentType)}
                                                    </Typography>
                                                    {attachment?.file?.filename && (
                                                        <Typography component="block" variant="caption" color="text"
                                                                    sx={{display: 'block'}}>
                                                            {t('incidentPanel.attachments.fileName')}: {attachment.file.filename}
                                                        </Typography>
                                                    )}
                                                    {attachment?.webLink && (
                                                        <Typography component="block" variant="caption" color="text"
                                                                    sx={{display: 'block'}}>
                                                            {t('incidentPanel.attachments.webLink')}: {attachment.webLink}
                                                        </Typography>
                                                    )}
                                                    <Typography component="block" variant="caption" color="text"
                                                                sx={{display: 'block'}}>
                                                        {t('incidentPanel.attachments.source')}: {getRegistryDescription('attachmentSource', attachment.attachmentSource)}
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </MDBox>
            )}
        </MDBox>
    );
};

export default IncidentPanel;
